
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Star, ChevronLeft } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";

const Review = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  // First fetch order details to get the meal_id
  const { data: orderData, isLoading } = useQuery({
    queryKey: ['review-order', orderId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get order details first
      const orderResponse = await supabase
        .from('orders')
        .select(`
          *,
          meal:meals (
            *,
            chef:profiles!meals_chef_id_fkey (
              first_name,
              last_name
            )
          )
        `)
        .eq('id', orderId)
        .single();

      if (orderResponse.error) throw orderResponse.error;

      // Check for existing review
      const reviewResponse = await supabase
        .from('reviews')
        .select('*')
        .eq('meal_id', orderResponse.data.meal_id)
        .eq('user_id', user.id)
        .maybeSingle();

      // If there's an existing review, set the initial values
      if (reviewResponse.data) {
        setRating(reviewResponse.data.rating);
        setComment(reviewResponse.data.comment || "");
      }

      return {
        order: orderResponse.data,
        existingReview: reviewResponse.data
      };
    }
  });

  const submitReview = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const reviewData = {
        meal_id: orderData?.order.meal_id,
        user_id: user.id,
        rating,
        comment
      };

      // If there's an existing review, update it
      if (orderData?.existingReview) {
        const { error } = await supabase
          .from('reviews')
          .update(reviewData)
          .eq('id', orderData.existingReview.id);

        if (error) throw error;
      } else {
        // Otherwise, insert a new review
        const { error } = await supabase
          .from('reviews')
          .insert([reviewData]);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meal', orderData?.order.meal_id] });
      toast({
        title: orderData?.existingReview ? "Review updated" : "Review submitted",
        description: orderData?.existingReview 
          ? "Your review has been updated successfully!"
          : "Thank you for your feedback!"
      });
      navigate(`/meal/${orderData?.order.meal_id}`);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        variant: "destructive"
      });
      console.error('Error submitting review:', error);
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <Card className="p-6 animate-pulse">
            <div className="h-6 bg-muted rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-muted rounded w-full mb-2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mr-2"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold">
            {orderData?.existingReview ? "Update Review" : "Leave a Review"}
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row gap-6 mb-6">
            {orderData?.order.meal.image && (
              <img 
                src={orderData.order.meal.image} 
                alt={orderData.order.meal.name} 
                className="w-full sm:w-48 h-48 object-cover rounded-lg"
              />
            )}
            <div>
              <h2 className="text-2xl font-bold mb-2">{orderData?.order.meal.name}</h2>
              <p className="text-muted-foreground">
                Chef {orderData?.order.meal.chef?.first_name} {orderData?.order.meal.chef?.last_name}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Rating</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className={`p-1 rounded-full transition-colors ${
                    rating >= star ? "text-yellow-400" : "text-gray-300"
                  }`}
                >
                  <Star className="h-8 w-8 fill-current" />
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Comment</label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience with this meal..."
              className="min-h-[100px]"
            />
          </div>

          <div className="flex gap-4">
            <Button
              onClick={() => submitReview.mutate()}
              disabled={!rating || submitReview.isPending}
            >
              {orderData?.existingReview ? "Update Review" : "Submit Review"}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Review;
